import { createEvents, EventAttributes } from "ics";
import { formatInTimeZone } from "date-fns-tz";
import type { EventDateDto, TicketPurchaseDto } from "@/models.g";
import { stripHtml, isIOS } from "@common/utils";

interface CalendarEventData {
  purchase: TicketPurchaseDto;
  confirmationCode: string;
  publicId: string;
  supportEmail?: string;
}

interface EventOccurrence {
  start: Date;
  end: Date;
}

export default function useAddToCalendar() {
  /**
   * Generates an iCalendar (.ics) file content for Apple Calendar and Microsoft Outlook.
   * Emits one VEVENT per occurrence: a single event for a normal purchase, or one per
   * date in coveredSeriesDates for a series purchase.
   */
  async function generateICS(data: CalendarEventData): Promise<string> {
    try {
      const { eventDate } = data.purchase;
      const occurrences = getEventOccurrences(data.purchase);
      const description = buildEventDescription(data);

      const events: EventAttributes[] = occurrences.map(({ start, end }) =>
        buildEventAttributes(
          start,
          end,
          eventDate,
          description,
          data.supportEmail,
        ),
      );

      // Generate .ics file content
      const { error, value } = createEvents(events);

      if (error) {
        console.error("Error generating .ics file:", error);
        throw new Error("Failed to generate calendar file. Please try again.");
      }

      if (!value) {
        throw new Error("Failed to generate calendar file content.");
      }

      return value;
    } catch (err) {
      console.error("Error in generateICS:", err);
      throw err instanceof Error
        ? err
        : new Error("Failed to generate calendar file");
    }
  }

  /**
   * Resolves the date(s) a calendar file should cover: one occurrence per date in
   * coveredSeriesDates for a series purchase, or the purchase's single eventDate otherwise.
   */
  function getEventOccurrences(purchase: TicketPurchaseDto): EventOccurrence[] {
    const { eventDate, eventSeriesId, coveredSeriesDates } = purchase;

    if (
      eventSeriesId != null &&
      coveredSeriesDates &&
      coveredSeriesDates.length > 0
    ) {
      return coveredSeriesDates.map((coveredDate) => {
        if (!coveredDate.startLocal || !coveredDate.endLocal) {
          throw new Error("Event start and end times are required");
        }
        return {
          start: new Date(coveredDate.startLocal),
          end: new Date(coveredDate.endLocal),
        };
      });
    }

    if (
      !eventDate?.eventStartDateTimeLocal ||
      !eventDate.eventEndDateTimeLocal
    ) {
      throw new Error("Event start and end times are required");
    }

    return [
      {
        start: new Date(eventDate.eventStartDateTimeLocal),
        end: new Date(eventDate.eventEndDateTimeLocal),
      },
    ];
  }

  /**
   * Builds the ics library's EventAttributes for a single occurrence.
   */
  function buildEventAttributes(
    start: Date,
    end: Date,
    eventDate: EventDateDto | null | undefined,
    description: string,
    supportEmail?: string,
  ): EventAttributes {
    const event: EventAttributes = {
      start: toUtcDateArray(start),
      startInputType: "utc",
      end: toUtcDateArray(end),
      endInputType: "utc",
      title: eventDate?.eventName || "Event",
      description: description,
      location: eventDate?.eventLocation || "",
      status: "CONFIRMED",
      busyStatus: "BUSY",
      // Only include organizer if email is available (per RFC 5545)
      ...(supportEmail && {
        organizer: {
          name: "Hagadone Ticketing",
          email: supportEmail,
        },
      }),
    };

    // Add boarding time reminder if applicable
    if (eventDate?.eventBoardingMinutes && eventDate.eventBoardingMinutes > 0) {
      event.alarms = [
        {
          action: "display",
          description: "Boarding time - Please arrive for event check-in",
          trigger: { minutes: eventDate.eventBoardingMinutes, before: true },
        },
      ];
    }

    return event;
  }

  /**
   * Converts a Date to the [year, month, day, hour, minute] UTC array the ics library expects.
   */
  function toUtcDateArray(
    date: Date,
  ): [number, number, number, number, number] {
    return [
      date.getUTCFullYear(),
      date.getUTCMonth() + 1, // ics library expects 1-based month
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
    ];
  }

  /**
   * Generates a Google Calendar URL for adding an event
   */
  function generateGoogleCalendarUrl(data: CalendarEventData): string {
    try {
      const { eventDate } = data.purchase;

      // Validate required data
      if (
        !eventDate?.eventStartDateTimeLocal ||
        !eventDate.eventEndDateTimeLocal
      ) {
        throw new Error("Event start and end times are required");
      }

      // Format dates as YYYYMMDDTHHmmssZ for Google Calendar
      const startUTC = formatInTimeZone(
        eventDate.eventStartDateTimeLocal,
        "UTC",
        "yyyyMMdd'T'HHmmss'Z'",
      );
      const endUTC = formatInTimeZone(
        eventDate.eventEndDateTimeLocal,
        "UTC",
        "yyyyMMdd'T'HHmmss'Z'",
      );

      // Build event description
      const description = buildEventDescription(data);

      // Build Google Calendar URL
      const params = new URLSearchParams({
        action: "TEMPLATE",
        text: eventDate.eventName || "Event",
        dates: `${startUTC}/${endUTC}`,
        details: description,
        location: eventDate.eventLocation || "",
      });

      // Add timezone if available
      if (eventDate.venueTimezone) {
        params.append("ctz", eventDate.venueTimezone);
      }

      return `https://calendar.google.com/calendar/render?${params.toString()}`;
    } catch (err) {
      console.error("Error generating Google Calendar URL:", err);
      throw err instanceof Error
        ? err
        : new Error("Failed to generate Google Calendar link");
    }
  }

  /**
   * Triggers download or opens .ics file based on device.
   * On iOS: Opens in new window to trigger Calendar app.
   * On desktop: Downloads file to user's download folder.
   */
  function downloadICS(filename: string, content: string): void {
    try {
      // Create blob with .ics content
      const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);

      // iOS: Open in new window to trigger Calendar app
      // (download attribute is ignored on iOS)
      if (isIOS()) {
        window.open(url, "_blank");

        // Cleanup after delay
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);

        return;
      }

      // Desktop/Android: Standard download
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error handling .ics file:", err);
      throw new Error(
        "Failed to open calendar file. Please check your browser settings.",
      );
    }
  }

  /**
   * Builds the event description with confirmation code and purchase link
   */
  function buildEventDescription(data: CalendarEventData): string {
    const { purchase, confirmationCode, publicId } = data;
    const { eventDate } = purchase;

    const parts: string[] = [];

    // Add event long description if available
    if (eventDate?.eventLongDescription) {
      const plainTextDescription = stripHtml(eventDate.eventLongDescription);
      if (plainTextDescription) {
        parts.push(plainTextDescription);
      }
    }

    // Add confirmation code
    parts.push(`Confirmation Code: ${confirmationCode}`);

    // Add purchase link
    const purchaseUrl = `${window.location.origin}/purchase/${publicId}`;
    parts.push(`View Your Purchase: ${purchaseUrl}`);

    // Add boarding time information if applicable
    if (eventDate?.eventBoardingMinutes && eventDate.eventBoardingMinutes > 0) {
      const boardingLabel = eventDate.eventBoardingLabel || "Boarding";
      parts.push(
        `${boardingLabel}: Please arrive ${eventDate.eventBoardingMinutes} minutes before event start`,
      );
    }

    return parts.join("\n\n");
  }

  /**
   * Generates a safe filename from event name and confirmation code
   */
  function generateFilename(
    eventName: string,
    confirmationCode: string,
  ): string {
    // Replace invalid filename characters with hyphens
    const safeName = eventName.replace(/[^a-z0-9]/gi, "-").replace(/-+/g, "-");
    return `${safeName}-${confirmationCode}.ics`;
  }

  return {
    generateICS,
    generateGoogleCalendarUrl,
    downloadICS,
    generateFilename,
  };
}
